import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { prefetchAdminRoutes } from '@/lib/prefetchAdminRoutes';

export default function AdminRoute({ children }: { children: React.ReactNode }) {
  const { session, loading: authLoading, isAdmin, adminChecked } = useAuth();
  const loading = authLoading || (!!session && !adminChecked);
  const queryClient = useQueryClient();

  // Once an admin lands on any admin page, eagerly load the JS chunks for
  // the other admin dashboards AND prefetch the heaviest shared data
  // payloads so any subsequent admin page renders with cached data.
  useEffect(() => {
    if (loading || !session || !isAdmin) return;
    prefetchAdminRoutes();

    const prefetchInvoke = <T,>(key: string, fn: string, transform: (d: any) => T) =>
      queryClient.prefetchQuery({
        queryKey: [key],
        queryFn: async () => {
          const { data, error } = await supabase.functions.invoke(fn);
          if (error) throw error;
          return transform(data);
        },
        staleTime: 60_000,
      });

    // Fire in parallel; ignore failures (each page handles its own errors).
    void prefetchInvoke('admin-users', 'admin-users', (d) => d);
    void prefetchInvoke('admin-transactions', 'admin-transactions', (d) => d?.transactions || []);
    void prefetchInvoke('admin-content-views', 'admin-content-views', (d) => d);
  }, [loading, session, isAdmin, queryClient]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!session || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
